import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { AuthStoreService, DialogNgbService } from '@motivforce/mx-library-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { InvoiceDetails } from 'src/app/core/model/lenovo/invoice-details';
import { UserStoreService } from 'src/app/core/store/user-store.service';

@UntilDestroy()
@Component({
  selector: 'app-invoice-upload-form',
  templateUrl: './invoice-upload-form.component.html',
  styleUrls: ['./invoice-upload-form.component.scss'],
})
export class InvoiceUploadFormComponent implements OnInit {
  @Input() isPcsd: boolean;
  @Input() checkBankDetails = false;
  @Input() submitSuccessMessage: string;

  form = this.formBuilder.group({
    invoiceNumber: ['', Validators.required],
    quarter: [{ value: null, disabled: true }, Validators.required],
    localCurrency: [{ value: null, disabled: true }, Validators.required],
    rebateAmountInLocalCurrency: [{ value: null, disabled: true }, Validators.required],
    invoiceFile: '',
  });

  quartersList: any[];

  details: InvoiceDetails;

  bankDetailsFormValid = false;

  selectedFile: File | undefined;

  isNA = false;
  isLA = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private userStore: UserStoreService,
    private dialogService: DialogNgbService,
    private translateService: TranslateService,
    private authStore: AuthStoreService
  ) {}

  ngOnInit(): void {
    if (this.authStore.userSettings) {
      const { regionCode } = (this.authStore.userSettings?.user as any).region;
      this.isNA = regionCode === 'NA';
      this.isLA = regionCode === 'LA';

      if (this.isNA) {
        this.form.get('invoiceNumber')!.clearValidators();
        this.form.get('invoiceNumber')!.updateValueAndValidity();
      }
      if (this.isLA) {
        this.form.addControl('hasInvoice', this.formBuilder.control(true));
        this.form.addControl('taxAmount1', this.formBuilder.control(''));
        this.form.addControl('taxAmount2', this.formBuilder.control(''));
      }
    }

    this.userStore.currentInvoiceDetails$
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((currentInvoiceDetails) => {
        this.details = currentInvoiceDetails.invoiceDetails!;
        this.form.patchValue(this.details);
      });
  }

  onFileSelected(event: any): void {
    const selectedFile: File = event.target.files[0];

    if (selectedFile.name.length > 50) {
      this.dialogService.openValidation(
        [this.translateService.instant(`Selected file name is longer that 50 characters.`)],
        this.translateService.instant('Error!')
      );
      return;
    }

    this.selectedFile = selectedFile;
  }

  onSubmit(): void {
    if (!this.form.valid) {
      this.dialogService.openValidation(
        [this.translateService.instant('Please complete all required fields')],
        this.translateService.instant('Important')
      );
      return;
    }

    if (this.isNA) {
      this.details.invoiceUrl = '';
      this.details.invoiceDate = new Date();
      this.submitClaim();
      return;
    }

    if (this.isLA) {
      const hasInvoiceCtrl = this.form.get('hasInvoice');

      if (hasInvoiceCtrl && hasInvoiceCtrl.value === false) {
        const { taxAmount1, taxAmount2 } = this.form.value;
        const rebate = this.details.rebateAmountInLocalCurrency;

        if (taxAmount1 === null || taxAmount1 === undefined || taxAmount1 === '') {
          this.dialogService.openValidation(
            [this.translateService.instant('Tax_amount_one_mandatory')],
            this.translateService.instant('Important')
          );
          return;
        }

        if (taxAmount1 < 0) {
          this.dialogService.openValidation(
            [this.translateService.instant('taxAmount_less_than_zero')],
            this.translateService.instant('Important')
          );
          return;
        }
        if (taxAmount1 > rebate) {
          this.dialogService.openValidation(
            [this.translateService.instant('taxAmount_greater_than_rebate')],
            this.translateService.instant('Important')
          );
          return;
        }
        if (taxAmount2 && taxAmount2 < 0) {
          this.dialogService.openValidation(
            [this.translateService.instant('taxAmount_two_less_than_zero')],
            this.translateService.instant('Important')
          );
          return;
        }
        if (taxAmount2 && taxAmount2 + taxAmount1 > rebate) {
          this.dialogService.openValidation(
            [this.translateService.instant('taxAmount_sum_greater_than_rebate')],
            this.translateService.instant('Important')
          );
          return;
        }
        this.details.invoiceUrl = '';
        this.details.invoiceDate = new Date();
        this.submitClaim();
        return;
      }

      if (this.form.contains('taxAmount1')) {
        this.form.removeControl('taxAmount1');
      }
      if (this.form.contains('taxAmount2')) {
        this.form.removeControl('taxAmount2');
      }
    }

    if (!this.selectedFile) {
      this.dialogService.openValidation(
        [this.translateService.instant('Please attach an invoice.')],
        this.translateService.instant('Important')
      );
      return;
    }

    this.userStore
      .uploadInvoiceFile({ ...this.details, ...this.form.value } as InvoiceDetails, this.selectedFile!)
      .then((result) => {
        this.details.invoiceUrl = result || '';
        this.details.invoiceDate = new Date();
        this.submitClaim();
      });
  }

  private submitClaim(): void {
    const invoice = { ...this.details, ...this.form.value } as InvoiceDetails;
    this.userStore.submitInvoice(invoice).then((success) => {
      if (success) {
        const defaultMessage = this.isNA ? 'You have submitted your rebate claim' : 'You have submitted your invoice';
        const message = (this.submitSuccessMessage || defaultMessage).replace('{{Quarter}}', invoice.quarter ?? '');
        this.dialogService.openNotification([message], 'Thank you!');
      }
    });
  }
}
